import React, { useMemo } from 'react';
import { FileText, MessageSquare, Paperclip, ShieldAlert, Gavel, CheckCircle2, Circle } from 'lucide-react';

// Derives a lifecycle timeline from a dispute + its message thread.
// No backend/schema change: milestones are computed from existing data,
// so it works for historical disputes too.
const fmt = (ts) => (ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);

const DisputeTimeline = ({ dispute, messages = [] }) => {
  const events = useMemo(() => {
    const msgs = [...(messages || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const firstProviderMsg = msgs.find((m) => m.sender_role === 'provider');
    const firstEvidenceMsg = msgs.find(
      (m) => (m.attachments && m.attachments.length > 0) || (m.file_attachments && m.file_attachments.length > 0)
    );
    const escalationMsg = msgs.find((m) => m.message_type === 'escalation');

    const resolvedAt = dispute.resolved_at || null;
    const closedAt = dispute.closed_at || (['resolved', 'closed'].includes(dispute.status) ? resolvedAt : null);
    const escalatedAt = dispute.escalated_at || (escalationMsg ? escalationMsg.created_at : null);

    return [
      {
        key: 'opened',
        label: 'Dispute opened',
        icon: FileText,
        ts: dispute.created_at,
        done: true,
        detail: dispute.client_name ? `by ${dispute.client_name}` : null,
      },
      {
        key: 'provider_replied',
        label: 'Provider replied',
        icon: MessageSquare,
        ts: firstProviderMsg ? firstProviderMsg.created_at : null,
        done: !!firstProviderMsg,
      },
      {
        key: 'evidence',
        label: 'Evidence uploaded',
        icon: Paperclip,
        ts: firstEvidenceMsg ? firstEvidenceMsg.created_at : null,
        done: !!firstEvidenceMsg,
      },
      {
        key: 'admin_assigned',
        label: 'Admin assigned',
        icon: ShieldAlert,
        ts: escalatedAt,
        done: !!dispute.escalated_to_admin,
      },
      {
        key: 'resolution',
        label: 'Resolution',
        icon: Gavel,
        ts: resolvedAt,
        done: !!resolvedAt,
        detail: dispute.resolution_type ? dispute.resolution_type.replace(/_/g, ' ') : null,
      },
      {
        key: 'closed',
        label: 'Closed',
        icon: CheckCircle2,
        ts: closedAt,
        done: !!closedAt,
      },
    ];
  }, [dispute, messages]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="dispute-timeline">
      <h3 className="font-bold text-gray-800 mb-4">Timeline</h3>
      <ol className="relative">
        {events.map((ev, idx) => {
          const Icon = ev.done ? ev.icon : Circle;
          const isLast = idx === events.length - 1;
          return (
            <li key={ev.key} className="flex gap-3 pb-5 last:pb-0" data-testid={`timeline-${ev.key}`}>
              {/* Rail: dot + connector line */}
              <div className="flex flex-col items-center">
                <span
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                    ev.done
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-white border-gray-300 text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                {!isLast && (
                  <span className={`w-0.5 flex-1 mt-1 ${ev.done ? 'bg-orange-300' : 'bg-gray-200'}`} style={{ minHeight: '1.25rem' }} />
                )}
              </div>
              {/* Content */}
              <div className="pt-1 flex-1">
                <p className={`text-sm font-semibold ${ev.done ? 'text-gray-800' : 'text-gray-400'}`}>{ev.label}</p>
                {ev.done ? (
                  <>
                    {ev.ts && <p className="text-xs text-gray-500">{fmt(ev.ts)}</p>}
                    {ev.detail && <p className="text-xs text-gray-500 capitalize">{ev.detail}</p>}
                  </>
                ) : (
                  <p className="text-xs text-gray-400 italic">Pending</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default DisputeTimeline;
